import React from 'react';
import { Box, useTheme } from '@mui/material';
import { Typography } from '@fdha/web-ui-library';
import { QuantitySelectorV2 } from '@fdha/web-ui-library';
import { MenuOptionSelection as MealItem } from '@fdha/graphql-api-patient';

import { getStyles } from './MealItem.styles';

interface MealItemProps {
  item: MealItem;
  isReadOnly: boolean;
  hasMultipleVariants?: (menuOptionId: string) => boolean;
  hasReachedRequired?: boolean;
  onQuantityChange: (item: MealItem, newQuantity: number) => void;
  onItemClick: (item: MealItem) => void;
}

const MealItemComponent: React.FC<MealItemProps> = ({
  item,
  isReadOnly,
  hasMultipleVariants,
  hasReachedRequired = false,
  onQuantityChange,
  onItemClick,
}) => {
  const theme = useTheme();
  const styles = getStyles(theme);

  const handleQuantityChange = (newQuantity: number) => {
    onQuantityChange(item, newQuantity);
  };

  const handleItemClick = () => {
    onItemClick(item);
  };

  const showIngredientName =
    item.mealkitVariantIngredientName &&
    hasMultipleVariants?.(item.menuOptionId);

  return (
    <Box
      key={
        item.mealkitVariantId
          ? `${item.menuOptionId}-${item.mealkitVariantId}`
          : item.menuOptionId
      }
      sx={styles.mealItemContainer}
    >
      <Box sx={styles.mealClickArea} onClick={handleItemClick}>
        {/* Meal Image */}
        <Box sx={styles.imageContainer}>
          {item.photoUrl ? (
            <Box
              component="img"
              src={item.photoUrl}
              alt={item.name}
              sx={styles.image}
            />
          ) : (
            <Box sx={styles.imagePlaceholder} />
          )}
        </Box>

        {/* Meal Info */}
        <Box sx={styles.mealInfo}>
          <Typography
            variant="body1"
            i18nKey="common:value"
            i18nParams={{ value: item.name }}
          >
            {item.name}
          </Typography>
          {showIngredientName && (
            <Typography
              variant="body2"
              sx={styles.variantText}
              i18nKey="meals:reviewDelivery.menuItem.withHelper"
              i18nComponents={{
                ingredientName: (
                  <Typography
                    variant="body2"
                    component="span"
                    sx={styles.variantText}
                    i18nKey="common:value"
                    i18nParams={{
                      value: item.mealkitVariantIngredientName || '',
                    }}
                  >
                    {item.mealkitVariantIngredientName}
                  </Typography>
                ),
              }}
            >
              with {item.mealkitVariantIngredientName}
            </Typography>
          )}
        </Box>
      </Box>

      <QuantitySelectorV2
        value={item.quantity}
        onChange={handleQuantityChange}
        isDisabled={isReadOnly}
        isDecrementDisabled={isReadOnly || item.quantity === 0}
        isIncrementDisabled={isReadOnly || hasReachedRequired}
        toggleDecrementIcon={false}
      />
    </Box>
  );
};

export default MealItemComponent;
